import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo } from "react";
import CardCommon from '@sg/components/CardCommon';
import ContentCard from '@sg/components/ContentCard';
import { Segment, Form, Button, notification } from "tea-component";
import { getGateReviewManagementList, getInDevProjectData, editInDevProjectData, editGateReviewManagement } from '@sg/services';
import MetricCard from '@/pages/strategic/pages/BusinessManagement/MetricCard';
import { STUDIO_IN_DEV_PROJECT_TOTAL_SPEND_CARD, STUDIO_IN_DEV_PROJECT_HEADCOUNT_CARD, COEFFICIENT } from '../../../config';
import { filterFontSizeReviewByKey } from '@sg/utils/commonPageMethod';
import { EDIT_PAGE_KEY_MAP } from '@/pages/strategic/hooks/useAutoSaveParams';
import {
  renderGateReviewTag,
  getReverseOptionsSegment,
} from '../../pageConfig';
import '../../index.scss';
import ProjectCalendar from './component/ProjectCalendar';
import Loading from '@intelligence/components/Loading';
import { getCurrentQuarters, toTwoDecimalNumber } from '@sg/utils/common';
import moment from 'moment';
import { useIntl } from 'umi';
import GrManagementFrom from "@sg/components/GrManagementFrom";
import useGRManagementAutoScroll from '../../hooks/useGRManagementAutoScroll';
import { useNotesAutoScroll } from '../../hooks/useNotesAutoScroll';
import { useGRManagementAutoSave } from '../../hooks/useGRManagementAutoSave';
import { getButtonPermission } from "@sg/_config/permission";
import { usePermissionReady } from '@sg/hooks/usePermissionReady';
import { createBtnPoint, SGBuriedBtnPoint, InDevProjectsTab } from '@/pages/strategic/utils/buriedPoint';
import { useInDevChartEyesState } from '../../hooks/useInDevChartEyesState';
import LifecycleFinancialForecastCard from './component/LifecycleFinancialForecastCard';
import SpendTrendCard from './component/SpendTrendCard';
import HeadcountRampCard from './component/HeadcountRampCard';
import { rendCardTitle } from './utils/rendCardTitle';
import AutoSaveModal from '@/pages/strategic/components/AutoSaveModal';
import RoutePrompt from '@/pages/strategic/components/RoutePrompt';
import PlainText from "@sg/components/PlainText";

interface IStudioProp {
  name: string;
  unified_id: string;
  studio_id?: string;
  originalData?: any;
}

interface IInDevProjectsInfoProps {
  /** 由容器 InDevProjects 传入的当前项目（name / unified_id / studio_id / originalData） */
  studio: IStudioProp;
  selectedMonth?: string | null;
  maxDataTime?: string;
  onEditingChange?: (isEditing: boolean) => void;
  onProjectChange?: (projectId: string | null) => void;
}

// forwardRef allows a child functional component to accept a ref from its parent
const InDevProjectsInfo = forwardRef<any, IInDevProjectsInfoProps>((props, ref) => {
  const { studio, selectedMonth, onEditingChange, onProjectChange, maxDataTime } = props;

  // Convert selectedMonth string to moment object for PlainText component
  const selectedMonthMoment = useMemo(() => {
    return selectedMonth ? moment(selectedMonth, 'YYYY-MM') : null;
  }, [selectedMonth]);

  const { formatMessage } = useIntl();
  const { ready: permissionReady } = usePermissionReady();
  const [selectedProject, setSelectedProject] = useState<{
    name: string;
    unified_id: string;
    studio_id: string;
    originalData?: any;
  }>({
    name: '',
    unified_id: '',
    studio_id: '',
    originalData: undefined,
  });

  const optionsSegment = getReverseOptionsSegment(formatMessage);

  const [activeDateTypeSegment, setActiveDateTypeSegment] = useState('month');

  // Edit mode state for GR Management
  const [isGREditing, setIsGREditing] = useState(false);

  // Additional editing states
  const [isNotesEditing, setIsNotesEditing] = useState(false);
  const [isLifecycleEditing, setIsLifecycleEditing] = useState(false);

  // Project data from API
  const [projectCardData, setProjectCardData] = useState<any>(null);

  // Memoized project information for buried point extInfo
  const projectInfo = useMemo(() => {
    const projectName = selectedProject?.name || 'Unknown';
    const projectId = selectedProject?.unified_id || '';
    return `Project: ${projectName}${projectId ? ` (ID: ${projectId})` : ''}`;
  }, [selectedProject?.name, selectedProject?.unified_id]);

  const { smallChartViewState, updateInDevEyesViewState } = useInDevChartEyesState();
  // Use spend-trend scrollability as single source of truth.
  // When spend trend shows scrollbar (>5 items in phase), headcount must
  // reserve matching bottom spacing to keep both charts visually aligned.
  const [isSpendTrendPhaseScrollable, setIsSpendTrendPhaseScrollable] = useState(false);

  // Edit mode handlers for GR Management
  const handleEdit = () => {
    // Track buried point for GR Management Edit button
    const btnPoint = createBtnPoint(
      InDevProjectsTab,
      'grManagementEdit',
      `GR Management Edit - ${projectInfo}`
    );
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }

    // Store original data when entering edit mode (before auto-save recovery)
    if (selectedMilestone) {
      setOriginalSelectedMilestone(JSON.parse(JSON.stringify(selectedMilestone)));
    }

    // Check for auto-save before entering edit mode
    handleGRManagementEdit(() => setIsGREditing(true));
  };

  const buttonPermission = useMemo(() => ({
    headcount: getButtonPermission('studio_inDevProjects_headcount'),
    lifeCycleFinancialsForecast: getButtonPermission('studio_inDevProjects_lifeCycleFinancialsForecast'),
    GRManagement: getButtonPermission('studio_inDevProjects_GRManagement'),
    notes: getButtonPermission('studio_inDevProjects_notes'),
  }), [permissionReady]);

  const handleSave = async () => {
    try {
      // 处理 kf_attachments 删除逻辑（添加 status: "deleted" 标记）
      const processAttachments = (originalMilestone: any, currentMilestone: any): string => {
        // 解析原始附件列表
        let originalList: any[] = [];
        try {
          if (originalMilestone?.kf_attachments && originalMilestone.kf_attachments.trim()) {
            originalList = JSON.parse(originalMilestone.kf_attachments);
          }
        } catch (e) {
          console.error('Failed to parse original attachments:', e);
        }

        // 解析新附件列表
        let newList: any[] = [];
        try {
          if (currentMilestone?.kf_attachments && currentMilestone.kf_attachments.trim()) {
            newList = JSON.parse(currentMilestone.kf_attachments);
          }
        } catch (e) {
          console.error('Failed to parse new attachments:', e);
        }

        // 创建一个 Set 来存储新附件中的文件路径（用于快速查找）
        const newFilePaths = new Set(newList.map((item: any) => item.path || item.file));

        // 找出原始列表中有但新列表中没有的附件，添加 status: "deleted"
        const deletedFiles = originalList
          .filter((item: any) => {
            const filePath = item.path || item.file;
            return !newFilePaths.has(filePath);
          })
          .map((item: any) => ({
            ...item,
            status: 'deleted'
          }));

        // 合并新附件和删除标记的附件
        const result = [...newList, ...deletedFiles];

        return JSON.stringify(result);
      };

      // Store original milestone for comparison (before any edits)
      const originalMilestone = milestoneData.find(
        (item: any) => item.milestone_id === selectedMilestone?.milestone_id
      );

      // 处理 gr_attachments - 直接使用新的附件列表，不需要删除标记
      const processedGrAttachments = selectedMilestone.gr_attachments || '';

      // 处理 kf_attachments - 需要添加删除标记
      const processedKfAttachments = processAttachments(originalMilestone, selectedMilestone);

      // 准备提交数据
      const finalData = {
        project_id: Number(selectedProject.unified_id) || '',
        milestone_id: selectedMilestone?.milestone_id,
        gate_review_decision: selectedMilestone?.gate_review_decision,
        gr_attachments: processedGrAttachments,
        key_feedback: selectedMilestone?.key_feedback,
        kf_attachments: processedKfAttachments,
      };

      await editGateReviewManagement(finalData);

      // 重新获取milestone列表数据
      const response = await getGateReviewManagementList({ project_id: parseInt(selectedProject.unified_id) });
      const updatedMilestoneData = response?.data || [];
      setMilestoneData(updatedMilestoneData);

      // 更新selectedMilestone为保存后的最新数据
      const updatedSelectedMilestone = updatedMilestoneData.find(
        (item: any) => item.milestone_id === selectedMilestone.milestone_id
      );
      if (updatedSelectedMilestone) {
        const normalizedMilestone = normalizeMilestoneData(updatedSelectedMilestone);
        setSelectedMilestone(normalizedMilestone);
        // Store original data after save (before auto-save recovery)
        setOriginalSelectedMilestone(JSON.parse(JSON.stringify(normalizedMilestone)));
      }

      // Clear auto-save after successful save
      clearGRManagementAutoSave();

      setIsGREditing(false);
    } catch (error) {
      console.error('Error saving GR Management:', error);
      notification.error({
        description: 'Failed to save Gate Review Management. Please try again.',
      });
    }
  };

  // Handle GR Management form changes - directly update selectedMilestone
  const handleSaveGRManagement = (value: any) => {
    setSelectedMilestone({
      ...selectedMilestone,
      gate_review_decision: value?.gate_review_decision,
      gr_attachments: value?.gr_attachments,
      key_feedback: value?.key_feedback,
      kf_attachments: value?.kf_attachments,
    });
  };

  const handleCancel = () => {
    // Check if there are changes by comparing current selectedMilestone with original
    if (originalSelectedMilestone && selectedMilestone) {
      console.log('🔄 [GR Management] originalSelectedMilestone', originalSelectedMilestone);
      console.log('🔄 [GR Management] selectedMilestone', selectedMilestone);
      const hasChanges = JSON.stringify(selectedMilestone) !== JSON.stringify(originalSelectedMilestone);

      if (hasChanges) {
        console.log('🔄 [GR Management] Changes detected on cancel, refetching data...');
        // Refetch data to get latest from API
        fetchGRManagementTimeline();
      } else {
        console.log('✅ [GR Management] No changes detected on cancel, skipping refetch');
        // Restore original data if no changes (similar to TableContent behavior)
        if (originalSelectedMilestone) {
          setSelectedMilestone(JSON.parse(JSON.stringify(originalSelectedMilestone)));
        }
      }
    }

    clearGRManagementAutoSave();
    setIsGREditing(false);
  };

  // Create card configs with API data using useMemo
  const spendCardConfig = useMemo(() => STUDIO_IN_DEV_PROJECT_TOTAL_SPEND_CARD(projectCardData), [projectCardData]);

  const headcountCardConfig = useMemo(() => STUDIO_IN_DEV_PROJECT_HEADCOUNT_CARD(projectCardData), [projectCardData]);

  // Fetch project data when project changes
  useEffect(() => {
    if (selectedProject.unified_id) {
      fetchProjectMetrics(selectedProject.unified_id);
    }
  }, [selectedProject.unified_id, selectedMonth]);

  // Exit editing mode when project changes
  useEffect(() => {
    // Exit editing mode for GR Management
    if (isGREditing) {
      setIsGREditing(false);
      clearGRManagementAutoSave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject.unified_id]);

  // const [HeadRateColumns, setHeadRateColumns] = useState(defaultHeadRate)

  const [milestoneData, setMilestoneData] = useState<any[]>([]);
  const [selectedMilestone, setSelectedMilestone] = useState<any>(null);
  const [originalSelectedMilestone, setOriginalSelectedMilestone] = useState<any>(null); // 保存原始数据用于对比
  const [loading, setLoading] = useState<boolean>(false);
  const grManagementRef = useRef<HTMLDivElement>(null);
  const notesRef = useRef<HTMLDivElement>(null);

  const topRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to GR Management when returning from preview (via sessionStorage)
  useGRManagementAutoScroll(grManagementRef, loading);

  // Auto-scroll to Notes when returning from document preview
  useNotesAutoScroll(notesRef);

  // Auto-save hook for GR Management
  const {
    autoSaveVisible: grAutoSaveVisible,
    handleGRManagementEdit,
    handleAutoSaveCancel: handleGRAutoSaveCancel,
    handleAutoSaveConfirm: handleGRAutoSaveConfirm,
    clearGRManagementAutoSave,
    hasUnsavedChanges: hasGRUnsavedChanges,
  } = useGRManagementAutoSave(
    isGREditing,
    selectedMilestone,
    selectedProject.unified_id,
    setSelectedMilestone
  );

  // Headcount Card editing state
  const [isHeadcountEditing, setIsHeadcountEditing] = useState(false);

  // Notify parent when project changes
  useEffect(() => {
    if (onProjectChange && selectedProject.unified_id) {
      onProjectChange(selectedProject.unified_id);
    }
  }, [selectedProject.unified_id, onProjectChange]);

  // Notify parent component when any editing state changes
  useEffect(() => {
    if (onEditingChange) {
      const isAnyEditing =
        isGREditing || isLifecycleEditing || isNotesEditing || isHeadcountEditing;
      onEditingChange(isAnyEditing);
    }
  }, [
    isGREditing,
    isLifecycleEditing,
    isNotesEditing,
    isHeadcountEditing,
    onEditingChange,
  ]);

  useEffect(() => {
    setTimeout(() => {
      topRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 400);
  }, []);

  // 👇 expose methods to parent
  useImperativeHandle(ref, () => ({
    scrollToGRManagement: () => {
      grManagementRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
  }));

  // Fetch project data for metric cards
  const fetchProjectMetrics = async (projectId: string) => {
    try {
      const payload: { project_id: string; datatime?: string } = { project_id: projectId };
      if (selectedMonth) {
        payload.datatime = selectedMonth;
      }
      const response = await getInDevProjectData(payload);

      const metricsData = response?.data;
      if (metricsData) {
        setProjectCardData(metricsData);
      }
    } catch (error) {
      console.error('Error fetching project metrics:', error);
      notification.error({
        description: 'Failed to fetch project metrics data'
      });
      setProjectCardData(null);
    }
  };
  // const extractAndUpdateDate = (period: string): string => {
  //   // 使用正则表达式提取第二个日期部分 (例如 '2025-05')
  //   const match = period.match(/(\d{4}-\d{2})\s~\s(\d{4}-\d{2})/);
  //   if (match) {
  //     // 获取第二个日期（例如 '2025-05'）
  //     const secondDate = match[2];
  //     // 构造新的日期，增加为 1 号
  //     return `${secondDate}-01`;
  //   }
  //   // 如果没有匹配到有效的日期格式，返回空字符串或者错误提示
  //   return '';
  // };
  const editProjectMetrics = async (value: any) => {
    // console.log('value', value);
    try {
      const datatime = projectCardData?.headcount?.datatime || moment().format('YYYY-MM'); // Default to current month if no datatime
      const updatedDate = `${datatime}-01`; // Backend returns "2025-10", append "-01"

      const apiData: any = {
        project_id: Number(selectedProject.unified_id) || '',
        // @ts-ignore
        studio_id: selectedProject.originalData.studio_id || '',
        metrics: [],
        datatime: datatime // Add datatime to API payload
      };

      // 仅当有值时才将相应的对象加入 metrics（undefined 和 null 不添加）
      if (value.dbs_planned_current_milestone !== undefined && value.dbs_planned_current_milestone !== null) {
        apiData.metrics.push({
          name: 'Current Milestone',
          value: value.dbs_planned_current_milestone,
          date: updatedDate,
          date_type: 'month'
        });
      }

      if (value.dbs_planned_next_milestone !== undefined && value.dbs_planned_next_milestone !== null) {
        apiData.metrics.push({
          name: 'Next Milestone',
          value: value.dbs_planned_next_milestone,
          date: updatedDate,
          date_type: 'month'
        });
      }

      await editInDevProjectData(apiData);
      await fetchProjectMetrics(selectedProject.unified_id);
      notification.success({
        description: 'Operate Successfully',
      });

    } catch (error) {
      console.error('Error editing project metrics:', error);
      notification.error({
        description: 'Failed to save project metrics. Please try again.',
      });
    }
  };
  // 同步容器传入的 studio 到内部 selectedProject，项目变化时重置 milestone
  useEffect(() => {
    if (!studio?.unified_id || studio.unified_id === 'all') {
      return;
    }
    setSelectedProject({
      name: studio.name || '',
      unified_id: studio.unified_id,
      studio_id: studio.studio_id || '',
      originalData: studio.originalData,
    });
    setSelectedMilestone(null);
  }, [studio?.unified_id]);

  // Fetch GR Management timeline data
  const fetchGRManagementTimeline = async () => {
    if (!selectedProject.unified_id) return;

    setLoading(true);
    try {
      const response = await getGateReviewManagementList({ project_id: parseInt(selectedProject.unified_id) });

      // 过滤历史字体大小与背景颜色
      const updatedMilestoneDataTemp = response?.data || [];
      const updatedMilestoneData = filterFontSizeReviewByKey(updatedMilestoneDataTemp, ['gate_review_decision', 'key_feedback']);
      // const updatedMilestoneData = response?.data || [];
      setMilestoneData(updatedMilestoneData);

      // Update selectedMilestone if it exists (to get latest data)
      if (selectedMilestone?.milestone_id) {
        const updatedSelectedMilestone = updatedMilestoneData.find(
          (item: any) => item.milestone_id === selectedMilestone.milestone_id
        );
        if (updatedSelectedMilestone) {
          const normalizedMilestone = normalizeMilestoneData(updatedSelectedMilestone);
          setSelectedMilestone(normalizedMilestone);
          // Store original data after fetch (before auto-save recovery)
          setOriginalSelectedMilestone(JSON.parse(JSON.stringify(normalizedMilestone)));
        }
      }
    } catch (error) {
      console.error('Failed to fetch gate review management data:', error);
      notification.error({
        description: 'Failed to fetch gate review management data'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedProject.unified_id) {
      fetchGRManagementTimeline();
    }
  }, [selectedProject.unified_id]);

  // Handle milestone card click
  const handleMilestoneCardClick = (
    milestoneId: string,
    triggerSource: 'auto' | 'click' = 'click',
  ) => {

    // Find the full milestone data
    const milestoneFullData = milestoneData.find(item => item.milestone_id === milestoneId);

    if (milestoneFullData) {

      // Track buried point only for real user click
      if (triggerSource === 'click') {
        const milestoneName = milestoneFullData?.milestone || 'Unknown';
        const milestoneIdValue = milestoneFullData?.milestone_id || milestoneId;
        const btnPoint = createBtnPoint(
          InDevProjectsTab,
          'milestoneCardClick',
          `Milestone Card Click - Milestone: ${milestoneName} (ID: ${milestoneIdValue}) - ${projectInfo}`
        );
        if (btnPoint) {
          SGBuriedBtnPoint(btnPoint);
        }
      }

      const normalizedMilestone = normalizeMilestoneData(milestoneFullData);
      setSelectedMilestone(normalizedMilestone);
      // Store original data when selecting a milestone (before entering edit mode)
      setOriginalSelectedMilestone(JSON.parse(JSON.stringify(normalizedMilestone)));
      // You can do whatever you want with the full data here
      // For example: open a modal, update state, navigate, etc.
    }
  };

  // Helper function to normalize JSON string format (consistent formatting) so that autosave feature can compare consistently
  const normalizeJsonString = (jsonStr: string): string => {
    if (!jsonStr || jsonStr.trim() === '') return '';
    try {
      const parsed = JSON.parse(jsonStr);
      return JSON.stringify(parsed); // Re-stringify to ensure consistent format without spaces
    } catch (error) {
      console.error('Error normalizing JSON string:', error);
      return jsonStr;
    }
  };

  // Helper function to normalize milestone data (standardize JSON string formats)
  const normalizeMilestoneData = (milestone: any) => {
    if (!milestone) return milestone;
    return {
      ...milestone,
      gr_attachments: normalizeJsonString(milestone.gr_attachments || ''),
      kf_attachments: normalizeJsonString(milestone.kf_attachments || ''),
    };
  };

  // Helper function to parse attachments JSON string
  const parseAttachments = (attachmentsStr: string) => {
    if (!attachmentsStr || attachmentsStr.trim() === '') return [];
    try {
      const parsed = JSON.parse(attachmentsStr);
      // Ensure it's an array and filter out any undefined/null values
      if (Array.isArray(parsed)) {
        return parsed
          .filter(item => item && typeof item === 'object' && item.file && item.path)
          .map(item => ({
            name: item.file, // Map 'file' to 'name' for ContentCard
            path: item.path, // Keep 'path' for ContentCard
            url: item.path,  // Also provide 'url' for backward compatibility
          }));
      }
      return [];
    } catch (error) {
      console.error('Error parsing attachments:', error);
      return [];
    }
  };

  return (
    <>
      <div className='dbs-home-studio-card'>
        <MetricCard
          key={spendCardConfig?.title}
          coefficient={COEFFICIENT}
          config={{
            ...spendCardConfig,
            titleVariable: selectedProject.name,
          }}
          capsuleMode={'single'}
          capsuleHeight={40}
        />
        <MetricCard
          key={`headcount-card-${selectedProject.unified_id}`}
          coefficient={COEFFICIENT}
          config={{
            ...headcountCardConfig,
            titleVariable: selectedProject.name,
          }}
          capsuleMode={'single'}
          capsuleHeight={40}
          editable={true}
          userPermissions={{ canEdit: buttonPermission.headcount }}
          editKey={['dbs_planned_current_milestone', 'dbs_planned_next_milestone']}
          onSave={editProjectMetrics}
          valueFormatter={toTwoDecimalNumber}
          autoSaveId={selectedProject.unified_id}
          autoSavePageKey={EDIT_PAGE_KEY_MAP.studio_indev_headcount_card}
          onEditingChange={setIsHeadcountEditing}
          onEditClick={() => {
            // Track buried point for Headcount Card Edit button
            const btnPoint = createBtnPoint(
              InDevProjectsTab,
              'headcountCardEdit',
              `Headcount Card Edit - ${projectInfo}`
            );
            if (btnPoint) {
              SGBuriedBtnPoint(btnPoint);
            }
          }}
        />
      </div>
      <LifecycleFinancialForecastCard
        projectId={selectedProject.unified_id}
        selectedMonth={selectedMonth ?? undefined}
        studioId={selectedProject.originalData?.studio_id}
        projectInfo={projectInfo}
        canEdit={buttonPermission.lifeCycleFinancialsForecast}
        onEditingChange={setIsLifecycleEditing}
      />

      <div ref={notesRef} style={{ position: 'relative', top: '-50px', margin: '20px' }}></div>
      <PlainText
        key={`plaintext-${selectedProject.unified_id}`}
        title={formatMessage({ id: 'dbs_notes' })}
        url="/api/v1/mgmt_pc/note/get"
        editUrl="/api/v1/mgmt_pc/note/update"
        params={{
          project_id: Number(selectedProject.unified_id),
          source: "indev",
          logical_flag: "has_date_latest"
        }}
        showDatePicker={false}
        selectedMonth={selectedMonthMoment}
        maxDataTime={maxDataTime}
        showTimeInTitle={true}
        editable={true}
        userPermissions={{ canEdit: buttonPermission.notes }}
        tipString={'Data is updated by the Studio Operations Management team.'}
        autoSavePageKey={EDIT_PAGE_KEY_MAP.studio_indev_notes}
        autoSaveId={selectedProject.unified_id}
        // File Upload
        allowFileUpload={true}
        uploadDomain="mgmt/studio/in_dev/notes"
        cosConfigDomain="mgmt/studio/in_dev/notes"
        onEditingChange={setIsNotesEditing}
        onEditClick={(type) => {
          // Track buried point for Notes Edit button or Add Content link click
          const extInfo = type === 'Add Content Link'
            ? `Notes Edit - Add Content Link - ${projectInfo}`
            : `Notes Edit - Edit Button - ${projectInfo}`;
          const btnPoint = createBtnPoint(
            InDevProjectsTab,
            'notesEdit',
            extInfo
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }}
      />

      <CardCommon
        title={<Segment
          className="dbs-gradient-segment"
          options={optionsSegment}
          value={activeDateTypeSegment}
          onChange={(value) => {
            // Track buried point for segment change
            const segmentType = value === 'month' ? 'By Month' : 'By Phase';
            const btnPoint = createBtnPoint(
              InDevProjectsTab,
              value === 'month' ? 'byMonth' : 'byPhase',
              `${segmentType} - ${projectInfo}`
            );
            if (btnPoint) {
              SGBuriedBtnPoint(btnPoint);
            }
            setActiveDateTypeSegment(value);
          }}
        />}
      >
        <div className='dbs-home-studio-card dbs-home-by-date-studio-card'>
          <SpendTrendCard
            projectId={selectedProject.unified_id}
            activeDateTypeSegment={activeDateTypeSegment}
            selectedMonth={selectedMonth}
            smallChartViewState={smallChartViewState}
            updateInDevEyesViewState={updateInDevEyesViewState}
            projectInfo={projectInfo}
            onPhaseScrollableChange={setIsSpendTrendPhaseScrollable}
          />

          {/* Headcount Ramp */}
          <HeadcountRampCard
            projectId={selectedProject.unified_id}
            activeDateTypeSegment={activeDateTypeSegment}
            selectedMonth={selectedMonth}
            smallChartViewState={smallChartViewState}
            updateInDevEyesViewState={updateInDevEyesViewState}
            projectInfo={projectInfo}
            isSpendTrendPhaseScrollable={isSpendTrendPhaseScrollable}
          />
        </div>
      </CardCommon>
      <div ref={grManagementRef} style={{ marginTop: '20px' }}>
        <CardCommon
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
              {rendCardTitle({
                title: `${formatMessage({ id: 'dbs_gr_management' })}${selectedMilestone?.milestone ? `_${selectedMilestone.milestone}` : ''}`,
                tips: selectedMilestone?.update_time ? `${formatMessage({ id: 'dbs_update_on' })} ${moment(selectedMilestone.update_time).format('YYYY-MM-DD')}` : ''
              })}
              {selectedMilestone && buttonPermission.GRManagement && !isGREditing && (
                <Button
                  onClick={handleEdit}
                  style={{ height: '30px', minWidth: '60px', fontSize: '12px', marginRight: 0 }}
                  className="dbs-gradient-btn"
                >
                  {formatMessage({ id: 'dbs_edit' })}
                </Button>
              )}
              {selectedMilestone && buttonPermission.GRManagement && isGREditing && (
                <div className="dbs-weekly-buttons-wrapper">
                  <Button
                    onClick={handleSave}
                    style={{ height: '30px', minWidth: '76px', fontSize: '12px' }}
                    className="dbs-gradient-btn"
                  >
                    {formatMessage({ id: 'dbs_confirm' })}
                  </Button>
                  <Button
                    onClick={handleCancel}
                    style={{ height: '30px', minWidth: '76px', fontSize: '12px' }}
                    className="dbs-outline-btn"
                  >
                    {formatMessage({ id: 'dbs_cancel' })}
                  </Button>
                </div>
              )}
            </div>
          }
        >
          {/* Gate Review Decision and Key Feedback Form */}

          {isGREditing ?
            <GrManagementFrom
              key={selectedMilestone?.milestone_id}
              data={selectedMilestone}
              onChange={handleSaveGRManagement}
              renderGateReviewTag={renderGateReviewTag}
              tableStyle={{ marginTop: '-14px' }}
            />
            : (<Form>
              <Form.Item label="Gate Review Decision">
                {renderGateReviewTag(selectedMilestone?.gate_review)}
                <ContentCard
                  domain="mgmt/studio/in_dev/gr_management"
                  items={selectedMilestone?.gate_review_decision || 'No data'}
                  files={parseAttachments(selectedMilestone?.gr_attachments)}
                  renderHtml={true}
                  fileStyling={true}
                  className="dbs-gr-management-content"
                  itemId={selectedProject.unified_id} // Pass project ID for scroll restoration
                  cosConfigDomain="gate_review_material,mgmt/studio/in_dev/gr_management"
                />
              </Form.Item>
              {/* Key Feedback Form */}
              <Form.Item label="Key Feedback">
                <ContentCard
                  domain="mgmt/studio/in_dev/gr_management"
                  items={selectedMilestone?.key_feedback || 'No data'}
                  files={parseAttachments(selectedMilestone?.kf_attachments)}
                  fileStyling={true}
                  renderHtml={true}
                  className="dbs-gr-management-content"
                  itemId={selectedProject.unified_id} // Pass project ID for scroll restoration
                  cosConfigDomain="gate_review_material,mgmt/studio/in_dev/gr_management"
                />
              </Form.Item>
            </Form>)}

          <div className='dbs-project-calendar-title'>
            {formatMessage({ id: 'dbs_project_calendar' })}
          </div>
          {loading ? (
            <Loading style={{ height: '110px' }} />
          ) :
            <ProjectCalendar projects={milestoneData} onCardClick={handleMilestoneCardClick} isEditing={isGREditing} selectedMonth={selectedMonth} />
          }
        </CardCommon>
      </div>

      {/* Auto-save Modal for GR Management */}
      <AutoSaveModal
        visible={grAutoSaveVisible}
        onConfirm={() => handleGRAutoSaveConfirm(() => setIsGREditing(true))}
        onClose={() => handleGRAutoSaveCancel(() => setIsGREditing(true))}
      />

      {/* RoutePrompt for GR Management unsaved changes */}
      <RoutePrompt
        when={hasGRUnsavedChanges}
        onOk={async () => {
          // Save GR Management changes before navigating away
          await handleSave();
        }}
      />
    </>
  );
});

InDevProjectsInfo.displayName = 'InDevProjectsInfo';

export default InDevProjectsInfo;
